import { useEffect, useState } from 'react';

import { Box, BoxProps, FormControl, FormLabel, InputBase, MenuItem, Select } from '@mui/material';

import { Country, useCountries } from 'src/hooks/useCountries';

interface ICustomInputProps {
    label?: string;
    placeholder?: string;
    boxStyling?: BoxProps;
    error?: string;
    type?: string;
    setFieldValue: (field, country) => void;
    field: string;
    selectedCountry: string;
    setSelectedCountry: (event: any) => void;
    formValue: string;
}

const CustomCountrySelect = ({
    boxStyling,
    label = 'Select Country',
    error,
    setFieldValue,
    field,
    selectedCountry,
    setSelectedCountry,
    placeholder = 'Enter/Search Country',
    formValue,
}: ICustomInputProps) => {
    const { countries: countryData, loadingCountries } = useCountries();
    const [countries, setCountries] = useState<Country[]>();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [inputCountry, setInputCountry] = useState<string>();

    const formatString = (str) => {
        return str.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
    };

    useEffect(() => {
        if (countryData && !loadingCountries) {
            setCountries(countryData);
        }
    }, [countryData, loadingCountries]);

    const handleDropdownChange = (event) => {
        setSelectedCountry(event.target.value);
        setFieldValue(field, event.target.value);
        setInputCountry(event.target.value);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputCountry(formatString(event.target.value));
        setSelectedCountry(formatString(event.target.value));
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const renderDropDownValue = (selected) => {
        const src = countries?.find(
            (country) => country.name === formatString(selectedCountry)
        )?.flag;
        return (
            <span
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {src && (
                    <img
                        src={src}
                        alt="country flag"
                        style={{
                            borderRadius: '50%',
                            height: '24px',
                            width: '24px',
                            objectFit: 'cover',
                        }}
                    />
                )}

                {isOpen && selected}
            </span>
        );
    };

    const handleInputBlur = () => {
        const foundCountry = countries.find(
            (country) => country.name === inputCountry || country.country === inputCountry
        );

        if (foundCountry) {
            setInputCountry(foundCountry.name);
            setFieldValue(field, foundCountry.name);
            return;
        }
        setSelectedCountry('');
    };

    const borderColor = error ? 'red' : '#F39E4E';

    return (
        <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
            <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                {label}
            </FormLabel>
            <Box
                display="flex"
                flexDirection="row"
                border={`1px solid ${borderColor}`}
                height="51px"
                borderRadius="8px"
                overflow="hidden"
                alignItems="center"
            >
                <FormControl sx={{ m: 1 }} variant="standard">
                    <Select
                        id="country select"
                        value={formValue}
                        onChange={handleDropdownChange}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        open={isOpen}
                        renderValue={(e) => renderDropDownValue(e)}
                        sx={{
                            '&:before': {
                                borderBottom: 'none',
                            },
                            '&:after': {
                                borderBottom: 'none',
                            },
                            '&:hover:not(.Mui-disabled):before': {
                                borderBottom: 'none',
                            },
                            marginTop: '16.75px',
                            marginBottom: '16.75px',
                            marginLeft: '28px',
                        }}
                        inputProps={{
                            'data-testid': 'country-select',
                        }}
                    >
                        {countries &&
                            !loadingCountries &&
                            countries.map((country) => (
                                <MenuItem key={country.name} value={country.name}>
                                    <img
                                        src={country.flag}
                                        alt="country flag"
                                        style={{ maxWidth: '15px', marginRight: '5px' }}
                                    />
                                    {country.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder={placeholder}
                    value={formValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    inputProps={{
                        'data-testid': 'country-input',
                    }}
                />
            </Box>
        </Box>
    );
};

export default CustomCountrySelect;
